import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  fnGetWhLocLotCtPltInv,
  fnGetWhSnInv,
  fnGetWhLocPjtLotCtPltInv,
  fnGetWhPjtSnInv,
  fnGetBarcodeScanCtPltNonReqInv,
} from "@shared/utils/InvUtils";
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_MOW_REG_005_POP_001 from "@menu/MTL/SN_MTL_MOW_REG_005/SN_MTL_MOW_REG_005_POP_001";
import SN_MTL_MOW_REG_005_POP_002 from "@menu/MTL/SN_MTL_MOW_REG_005/SN_MTL_MOW_REG_005_POP_002";
import SN_MTL_MOW_REG_005_POP_003 from "@menu/MTL/SN_MTL_MOW_REG_005/SN_MTL_MOW_REG_005_POP_003";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnNumberBox from "@shared/components/SnNumberBox";
import SnTextBox from "@shared/components/SnTextBox";
import SnLabel from "@shared/components/SnLabel";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MOW_REG_005 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);
  const txtDirectBarcodeRef = useRef<any>(null);
  const numberBoxFifoRef = useRef<any>(null);
  let isDirectScan = useRef<any>(false);

  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);

  // 26.09.22 kdy MAS0002(프로젝트 사용유무). 이 화면은 프로젝트가 헤더가 아니라 라인(grid2)에 있어
  // 재고 조회·캐시·필터를 라인의 cd_pjt 기준으로 나눈다. 도착 프로젝트는 없다(출발 프로젝트 그대로 이동).
  const mas0002 = config?.MAS0002?.dc_ctrl ?? "N"; // 프로젝트 사용유무 Y/N
  //#endregion

  //#region 프로젝트 재고 헬퍼
  // 재고 캐시(lotSnData)·grid4 매칭 키. 프로젝트 사용 시 cd_pjt 까지 같아야 같은 재고로 본다.
  const sameInvKey = (a: any, b: any) =>
    a?.cd_item === b?.cd_item &&
    a?.cd_wh === b?.cd_wh &&
    (mas0002 !== "Y" || (a?.cd_pjt ?? "") === (b?.cd_pjt ?? ""));

  // grid3 필터 조건에 붙일 프로젝트 조건
  const pjtFilter = (cd_pjt: any) =>
    mas0002 === "Y" ? { cd_pjt: cd_pjt ?? "" } : {};

  // 프로젝트 재고 API 는 요청 최상위에 cd_pjt 하나만 받으므로 프로젝트별로 나눠 호출한다.
  const callByPjt = async (list: any[], api: (p: any) => Promise<any>) => {
    const groups = new Map<string, any[]>();
    list.forEach((it) => {
      const key = it.cd_pjt ?? "";
      groups.set(key, [...(groups.get(key) ?? []), { ...it, cd_pjt: key }]);
    });
    const results = await Promise.all(
      Array.from(groups.entries()).map(async ([key, group]) => {
        const rows: any[] = (await api(group)) ?? [];
        return rows.map((r) => ({ ...r, cd_pjt: r.cd_pjt ?? key }));
      })
    );
    return results.flat();
  };

  // 프로젝트 사용 시 프로젝트 재고, 아니면 기존 재고 API (이 화면은 품목 유효기간 체크 true)
  const fetchLotInv = async (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (mas0002 !== "Y") return fnGetWhLocLotCtPltInv(list, true);
    return callByPjt(list, (g) => fnGetWhLocPjtLotCtPltInv(g, true));
  };
  const fetchSnInv = async (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (mas0002 !== "Y") return fnGetWhSnInv(list, true);
    return callByPjt(list, (g) => fnGetWhPjtSnInv(g, true));
  };
  //#endregion

  // 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 작업지시 투집자재 리스트
  const [wobillLineData, setWobillLineData] = useState<Record<string, any>[]>(
    []
  );
  const wobillLineDataRef = useRef<Record<string, any>[]>([]);
  const [woNoText, setWoNoText] = useState(""); // 작지 SnTextBox 값

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid4: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "5", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  useEffect(() => {
    wobillLineDataRef.current = wobillLineData;
  }, [wobillLineData]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mow_reg_005_01",
        params: searchDataToUse,
      };

      setLotSnData([]);
      resetWobillData();

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: [],
        grid4: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    // 삭제할 헤더의 no_io로 출고정보를 필터한다.
    getGridCurrent("grid4").applyFilters({
      no_io: focusedRowNodeH.data.no_io,
    });

    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL.data.state === "insert") {
      resetRowData();
      // 신규 등록중인 품목이 전부지워졌다고 판단하면 불러온 재고정보 지움
      let cnt = 0;
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (
          node.data.state === "insert" &&
          node.data.cd_item === focusedRowNodeL.data.cd_item
        ) {
          cnt++;
        }
      });
      if (cnt === 1) {
        setLotSnData((prev) =>
          prev.filter((item) => !sameInvKey(item, focusedRowNodeL.data))
        );
      }
    } else {
      getGridCurrent("grid4").deleteAllRows();
    }

    getGridCurrent("grid2").deleteSelectedRows();

    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const qtiolotsnChanges = getGridCurrent("grid4").getChange();

      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 11,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid4"],
        ],
        inserts: [qtiohChanges, qtioChanges, qtiolotsnChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
        qtio_lotsn: qtiolotsnChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MOW_REG_005_SAVE_01", SaveData);
      if (!ok) {
        // 저장 실패(예: ET001) 시 그리드에서 낙관적으로 지운 행이 DB엔 살아있어
        // "삭제된 것처럼 보이지만 실제로는 남아있는" 유령삭제가 생긴다. 재조회로 동기화.
        await onSearch(undefined);
        return false;
      }

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();

      // 중계서버 연동키(no_io_if_key)를 재조회 없이 해당 행에 반영
      if (Array.isArray(ok)) {
        ok.forEach((item: any) => {
          getGridCurrent("grid1").forEachNode((node: RowNode) => {
            if (node.data.no_io === item.no_io) {
              node.updateData({
                ...node.data,
                no_io_if_key: item.no_io_if_key,
              });
            }
          });
        });
      }

      setLotSnData([]);
      resetWobillData();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        cd_io: "top1",
        fg_trns: "1",
        rt_exch: 1,
        cd_exch: "000",
        tp_um: "001",
        tp_slip: "5",
        fg_slip: "2",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        no_hst: 0,
        no_io: "",
        cd_wh: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");
        rowNode.setDataValue("fg_map", ioType.fg_map, "noEvent");

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue(colId, rowData.cd_io, "noEvent");
            node.setDataValue("fg_io", ioType.fg_io, "noEvent");
            node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            node.setDataValue("fg_map", ioType.fg_map, "noEvent");
          });

          getGridCurrent("grid4").forEachNode((node: RowNode) => {
            if (node.data.no_io === rowData.no_io) {
              node.setDataValue(colId, rowData.cd_io, "noEvent");
            }
          });
        }
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.dt_io, "noEvent");
        });
        getGridCurrent("grid4").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue(colId, rowData.dt_io, "noEvent");
          }
        });
        break;
      case "cd_wh":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_wh, "noEvent");
        });
        break;
      case "cd_wh_ref":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_wh_ref, "noEvent");
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid4").applyFilters({
          no_io: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };

  const onGridHBtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    const lotItemList: any = [];
    const snItemList: any = [];

    // searchPanel에서 선택된 값 전달
    const selectedFormData = searchRef.current.getFormdata();
    const selectedData = {
      cd_biz: selectedFormData.cd_biz,
      dt_from: selectedFormData.dt_from,
      dt_to: selectedFormData.dt_to,
    };

    // 신규행 포커스 잡아주기
    let focusedRowHIdx = 0;
    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (node.data.state === "insert") {
        focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
      }
    });

    getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

    switch (id) {
      case "btn_POP1":
        {
          const component = (
            <SN_MTL_MOW_REG_005_POP_001 selectedData={selectedData} />
          );
          const result = await showResizablePopupModal({
            title: "불출요청적용",
            children: component,
            validation: true,
          });

          if (!result || result === "close") return;

          const headerResult = result[0];
          const lineResult = result[1];

          // 요청도움창 Header 결과 적용
          const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
          if (focusedRowNodeH && headerResult[0]) {
            for (const key in headerResult[0]) {
              switch (key) {
                case "cd_wh":
                case "nm_wh":
                case "cd_wh_ref":
                case "nm_wh_ref":
                case "cd_io":
                  focusedRowNodeH.setDataValue(key, headerResult[0][key]);
                  break;
              }
            }
          }

          // 요청도움창 Line 결과 적용
          lineResult.map((result: any) => {
            if (headerResult[0]) {
              const addedNode = onAddRow();
              if (addedNode) {
                for (const key in result) {
                  switch (key) {
                    case "no_line":
                      addedNode.setDataValue(
                        "no_line_req",
                        result.no_line,
                        "noEvent"
                      );
                      break;
                    default:
                      addedNode.setDataValue(key, result[key], "noEvent");
                      break;
                  }
                }
                addedNode.setDataValue("yn_req", "Y", "noEvent");
                addedNode.setDataValue(
                  "cd_wh",
                  headerResult[0].cd_wh,
                  "noEvent"
                );
                addedNode.setDataValue(
                  "cd_wh_ref",
                  headerResult[0].cd_wh_ref,
                  "noEvent"
                );
                if (result.fg_unit === "QT") {
                  addedNode.setDataValue(
                    "qt_good",
                    result.qt_remain,
                    "noEvent"
                  );
                  addedNode.setDataValue("qt", result.qt_remain, "noEvent");
                } else {
                  addedNode.setDataValue("qt", 0, "noEvent");
                }
                addedNode.setDataValue("qt_req", result.qt_remain, "noEvent");

                // 프로젝트 사용(MAS0002=Y) 시 라인에 프로젝트가 없으면 재고를 불러오지 않는다
                const hasPjt =
                  mas0002 !== "Y" || converter.getString(result.cd_pjt) !== "";
                if (hasPjt) {
                  switch (result.fg_unit) {
                    case "LOT":
                      lotItemList.push({
                        cd_biz: focusedRowNodeH.data.cd_biz,
                        cd_item: result.cd_item,
                        cd_wh: headerResult[0].cd_wh,
                        cd_loc: "",
                        cd_pjt: result.cd_pjt ?? "", // 라인의 프로젝트
                      });
                      break;
                    case "SN":
                      snItemList.push({
                        cd_biz: focusedRowNodeH.data.cd_biz,
                        cd_item: result.cd_item,
                        cd_wh: headerResult[0].cd_wh,
                        cd_pjt: result.cd_pjt ?? "", // 라인의 프로젝트
                      });
                      break;
                  }
                }

                getGridCurrent("grid2").applyBlock(addedNode);
              }
            }
          });
          getGridCurrent("grid1").applyBlock(focusedRowNodeH);
        }
        break;
      case "btn_POP2":
        // H 필수값 체크
        const requiredValuesCheckH =
          getGridCurrent("grid1").requiredValuesCheck();
        if (!requiredValuesCheckH) {
          return false;
        }

        const component = (
          <SN_MTL_MOW_REG_005_POP_002 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "작업지시적용",
          children: component,
        });

        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        // 요청도움창 Header 결과 적용
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            switch (key) {
              case "cd_wh":
              case "nm_wh":
              case "cd_wh_ref":
              case "nm_wh_ref":
              case "cd_io":
                focusedRowNodeH.setDataValue(key, headerResult[0][key]);
                break;
            }
          }
        }

        // 요청도움창 Line 결과 적용
        lineResult.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow();
            if (addedNode) {
              for (const key in result) {
                switch (key) {
                  case "no_wo":
                    addedNode.setDataValue("no_order", result.no_wo, "noEvent");
                    break;
                  case "no_line":
                    addedNode.setDataValue(
                      "no_line_order",
                      result.no_line,
                      "noEvent"
                    );
                    break;
                  default:
                    addedNode.setDataValue(key, result[key], "noEvent");
                    break;
                }
              }
              addedNode.setDataValue("yn_req", "Y", "noEvent");
              if (result.fg_unit === "QT") {
                addedNode.setDataValue("qt_good", result.qt_remain, "noEvent");
                addedNode.setDataValue("qt", result.qt_remain, "noEvent");
              } else {
                addedNode.setDataValue("qt", 0, "noEvent");
              }
              addedNode.setDataValue("qt_req", result.qt_remain, "noEvent");

              // 26.09.22 kdy 작업지시의 프로젝트는 팝업 상단(작업지시 헤더)에 있으므로 라인으로 내려준다
              const woPjt = converter.getString(headerResult[0].cd_pjt);
              if (mas0002 === "Y") {
                addedNode.setDataValue("cd_pjt", woPjt, "noEvent");
              }

              // 프로젝트 사용(MAS0002=Y) 시 프로젝트가 없으면 재고를 불러오지 않는다
              const hasPjt = mas0002 !== "Y" || woPjt !== "";
              if (hasPjt) {
                switch (result.fg_unit) {
                  case "LOT":
                    lotItemList.push({
                      cd_biz: focusedRowNodeH.data.cd_biz,
                      cd_item: result.cd_item,
                      cd_wh: focusedRowNodeH.data.cd_wh,
                      cd_loc: "",
                      cd_pjt: woPjt, // 작업지시 헤더의 프로젝트
                    });
                    break;
                  case "SN":
                    snItemList.push({
                      cd_biz: focusedRowNodeH.data.cd_biz,
                      cd_item: result.cd_item,
                      cd_wh: focusedRowNodeH.data.cd_wh,
                      cd_pjt: woPjt, // 작업지시 헤더의 프로젝트
                    });
                    break;
                }
              }

              getGridCurrent("grid2").applyBlock(addedNode);
            }
          }
        });
        getGridCurrent("grid1").applyBlock(focusedRowNodeH);
        break;
    }

    if (lotItemList.length > 0) {
      const searchLotData = await fetchLotInv(lotItemList);
      const lotData = searchLotData.map((item: any) => {
        const { no_lot, ...rest } = item; // no_lot만 추출
        return {
          ...rest,
          no_barcode: no_lot, // 필드명 바꾸기
          fg_unit: "LOT", // LOT/SN구분
          qt_isu: 0, // 적용량 추가
        };
      });

      setLotSnData((prevData: any[]) => {
        // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
        const newData = lotData.filter(
          (newItem: any) =>
            !prevData.some((oldItem: any) => sameInvKey(oldItem, newItem))
        );

        // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
        return newData.length === 0 ? prevData : [...prevData, ...newData];
      });
    }
    if (snItemList.length > 0) {
      const searchSnData = await fetchSnInv(snItemList);
      const snData = searchSnData.map((item: any) => {
        const { no_sn, ...rest } = item;
        return {
          ...rest,
          no_barcode: no_sn, // 필드명 바꾸기
          fg_unit: "SN", // LOT/SN구분
          no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
          no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
          qt_isu: 1, // 적용량 추가
          no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
          no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        };
      });

      setLotSnData((prevData: any[]) => {
        // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
        const newData = snData.filter(
          (newItem: any) =>
            !prevData.some((oldItem: any) => sameInvKey(oldItem, newItem))
        );

        // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
        return newData.length === 0 ? prevData : [...prevData, ...newData];
      });
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // 통합메뉴는 출고창고 못바꾸게 통제
      getGridCurrent("grid1").applyBlock(focusedRowNodeH);

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        dt_io: focusedRowNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        no_io: focusedRowNodeH.data?.no_io,
        cd_io: focusedRowNodeH.data?.cd_io,
        fg_slip: focusedRowNodeH.data?.fg_slip,
        tp_slip: focusedRowNodeH.data?.tp_slip,
        fg_io: focusedRowNodeH.data?.fg_io,
        fg_money: focusedRowNodeH.data?.fg_money,
        fg_vat: "11",
        rt_exch: focusedRowNodeH.data?.rt_exch,
        // 비용구분. 자재불출(통합)은 항상 '2'. 헤더에 fg_map 이 없을 때(조회SQL 누락 대비) '2' 로 폴백.
        fg_map: focusedRowNodeH.data?.fg_map ?? "2",
        qt: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        um: 0,
        um_k: 0,
        um_supply: 0,
        qt_good: 0,
        qt_bad: 0,
        cd_wh: focusedRowNodeH.data?.cd_wh,
        cd_wh_ref: focusedRowNodeH.data?.cd_wh_ref,
        yn_req: "N",
        qt_req: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  //#region linkWobillLine
  const linkWobillLine = (rowNode: any, cdItem: string) => {
    if (!rowNode) return;

    // 작업지시 투입자재 리스트에서 동일 품목 첫 건을 연결
    const wobillLines = wobillLineDataRef.current;
    const wobillLine = wobillLines.find((line: any) => line.cd_item === cdItem);

    if (wobillLine) {
      rowNode.setDataValue("no_order", wobillLine.no_wo, "noEvent");
      rowNode.setDataValue("no_line_order", wobillLine.no_line, "noEvent");
    } else if (wobillLines.length > 0) {
      const noWo = wobillLines[0].no_wo;

      // 리스트에 없는 품목은 리스트/그리드 항번 중 큰 값 + 1로 채번
      const maxListNoLine = wobillLines.reduce(
        (max: number, line: any) => Math.max(max, Number(line.no_line) || 0),
        0
      );

      // 그리드는 동일 작업지시(no_order)가 걸린 행만 대상
      let maxGridNoLine = 0;
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (node.data?.no_order === noWo) {
          maxGridNoLine = Math.max(
            maxGridNoLine,
            Number(node.data.no_line_order) || 0
          );
        }
      });

      rowNode.setDataValue("no_order", noWo, "noEvent");
      rowNode.setDataValue(
        "no_line_order",
        Math.max(maxListNoLine, maxGridNoLine) + 1,
        "noEvent"
      );
    }
  };
  //#endregion

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    // 품목(또는 프로젝트) 바뀌면 수량 초기화
    switch (colId) {
      case "cd_item":
      case "cd_pjt":
        rowData.qt = 0;
        rowData.qt_good = 0;
        rowData.qt_bad = 0;
        break;
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad, "noEvent");
        break;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    switch (colId) {
      case "cd_item":
      case "cd_pjt": // 26.09.22 kdy 프로젝트가 라인에 있어 프로젝트 변경도 재고 재조회
        if (colId === "cd_pjt" && mas0002 !== "Y") break;
        if (
          rowData.cd_item !== "" &&
          rowData.cd_item !== null &&
          rowData.cd_item !== undefined &&
          focusedRowNodeH.data.cd_wh !== "" &&
          focusedRowNodeH.data.cd_wh !== null &&
          focusedRowNodeH.data.cd_wh !== undefined
        ) {
          // 품목이나 창고가 바뀌면 꼬일 수 있으므로 초기화
          rowData.qt = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          resetRowData();

          linkWobillLine(rowNode, rowData.cd_item);

          // 프로젝트 사용(MAS0002=Y) 시에는 라인의 프로젝트까지 있어야 재고를 불러온다
          // (작업지시 연결은 위에서 품목+창고만으로 처리)
          const hasPjt =
            mas0002 !== "Y" || converter.getString(rowData.cd_pjt) !== "";

          const data = {
            cd_biz: rowData.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: focusedRowNodeH.data.cd_wh,
            cd_loc: "",
            cd_pjt: rowData.cd_pjt ?? "",
          };

          if (
            hasPjt &&
            !lotSnDataRef.current.some((item) => sameInvKey(item, data))
          ) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fetchLotInv(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some((item) =>
                    sameInvKey(item, data)
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...lotData];
                });
                break;
              case "SN":
                const searchSnData = await fetchSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    qt_isu: 1, // 적용량 추가
                    no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some((item) =>
                    sameInvKey(item, data)
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...snData];
                });
                break;
            }
          }
          // 재고리스트 필터돌게
          getGridCurrent("grid2").clearFocus();
          getGridCurrent("grid2").selectAndFocusRow(rowNode.rowIndex);
        } else {
          resetRowData();
        }
        break;
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      if (selectedRow.state === "insert") {
        getGridCurrent("grid3").applyFilters({
          cd_item: selectedRow.cd_item,
          cd_wh: selectedRow.cd_wh,
          ...pjtFilter(selectedRow.cd_pjt),
          qt_inv: "notZero",
        });

        const qtRemain = selectedRow.qt_req - selectedRow.qt;

        if (qtRemain > 0 && selectedRow.fg_unit !== "QT") {
          numberBoxFifoRef.current.setValue(qtRemain);
        } else {
          numberBoxFifoRef.current.setValue(0);
        }
      } else {
        getGridCurrent("grid3").applyFilters({
          cd_item: "",
          cd_wh: "",
          ...pjtFilter(""),
        });
      }
    } else {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region GridLotSn Events
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0, "noEvent");
      }
    }
  };

  const onRowDataUpdatedLotSn = () => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    // 신규일 때만 바코드 컴포넌트 포커스 타게
    if (focusedRowNodeL) {
      if (focusedRowNodeL.data.state === "insert") {
        if (!isDirectScan.current) {
          txtBarcodeRef.current.focus();
        } else {
          txtDirectBarcodeRef.current?.focus();
          isDirectScan.current = false;
        }
      }
    }
  };

  const onModelUpdatedLotSn = () => {
    // 적용팝업 푸터갱신
    getGridCurrent("grid3").refreshFooter();
  };
  //#endregion

  //#region GridD Events
  const onCellValueChangedD = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const oldValue = event.oldValue;
    const newValue = event.newValue;

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    switch (colId) {
      case "qt_good":
        if (newValue > oldValue) {
          await showDialog("alter", "기존 수량 보다 크게 입력했습니다.");
          rowNode.setDataValue(colId, oldValue, "noEvent");
        } else {
          const subValue = oldValue - newValue;

          getGridCurrent("grid3").forEachNode((node: RowNode) => {
            if (
              sameInvKey(node.data, rowData) &&
              node.data.no_barcode === rowData.no_barcode &&
              node.data.dt_io === rowData.dt_io_origin &&
              node.data.no_lot_cust === rowData.no_lot_cust
            ) {
              node.setDataValue("qt_inv", node.data.qt_inv + subValue);

              focusedRowNodeL.setDataValue(
                "qt_good",
                focusedRowNodeL.data.qt_good - subValue
              );
              return;
            }
          });
          // 0이면 행삭제
          if (newValue === 0) {
            getGridCurrent("grid4").deleteSelectedRows();
          }
          getGridCurrent("grid3").applyFilters({
            cd_item: focusedRowNodeL.data.cd_item,
            cd_wh: focusedRowNodeL.data.cd_wh,
            ...pjtFilter(focusedRowNodeL.data.cd_pjt),
            qt_inv: "notZero",
          });
        }
        break;
    }
  };

  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        if (focusedRowNodeL.data.state !== "insert") {
          await showDialog("alter", "신규건만 삭제할 수 있습니다.");
          return;
        }

        const focusedNodeD = getGridCurrent("grid4").focusedRowNode();

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            sameInvKey(node.data, focusedNodeD.data) &&
            node.data.no_barcode === focusedNodeD.data?.no_barcode &&
            node.data.dt_io === focusedNodeD.data?.dt_io_origin
          ) {
            node.setDataValue(
              "qt_inv",
              node.data.qt_inv + focusedNodeD.data?.qt_good
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_good",
              focusedRowNodeL.data?.qt_good - focusedNodeD.data?.qt_good
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          ...pjtFilter(focusedRowNodeL.data.cd_pjt),
          qt_inv: "notZero",
        });
        break;
    }
  };
  //#endregion

  //#region onDirectBarcodeEnter
  const onDirectBarcodeEnter = async (e: any) => {
    const barcode = e;

    if (converter.getString(barcode) === "") {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return;
    } else if (focusedRowNodeH.data.cd_wh === "") {
      await showDialog("alter", "출고창고가 선택되지 않았습니다.");
      return;
    }

    isDirectScan.current = true;

    const cdWh = focusedRowNodeH.data.cd_wh;

    const params = {
      cd_biz: focusedRowNodeH.data.cd_biz,
      cd_wh: cdWh,
      no_barcode: barcode,
    };

    const scanedList: any[] = [];

    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      scanedList.push(node.data.no_barcode);
    });

    const searchBarcodeData = await fnGetBarcodeScanCtPltNonReqInv(
      params,
      scanedList,
      true
    );

    if (!searchBarcodeData) return;

    if (searchBarcodeData.length < 1) {
      await showDialog("alter", "재고 정보가 없거나 잘못된 바코드입니다.");
      return;
    }

    const scannedBarcodes = searchBarcodeData.map((item: any) => {
      return item.no_lot !== "" ? item.no_lot : item.no_sn;
    });

    let hasInvalid = false;
    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      if (scannedBarcodes.includes(node.data.no_barcode)) {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      await showDialog("alter", "이미 스캔한 바코드 정보입니다.");
      return;
    }

    let addedItem = false;
    let itemNodeData: any;
    let itemNode: any;

    // 같은 품목 신규등록 중인 행이 있는지 찾기
    getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
      if (addedItem) return;

      if (
        node.data.cd_item === searchBarcodeData[0].cd_item &&
        node.data.state === "insert"
      ) {
        addedItem = true;
        itemNodeData = node.data;
        itemNode = node;
      }
    });

    let qtInv = 0;
    const cdItem = searchBarcodeData[0].cd_item;
    // 데이터가 어떻게 돼있을지 모르므로 0번행 품목기준으로만 보게
    searchBarcodeData.forEach((item: any) => {
      if (item.cd_item === cdItem) {
        qtInv = item.qt_inv;
      }
    });

    // 없는 품목이면 자동추가
    if (!addedItem) {
      const addedNode = getGridCurrent("grid2").addRow();
      addedNode?.setDataValue(
        "cd_item",
        searchBarcodeData[0].cd_item,
        "noEvent"
      );
      addedNode?.setDataValue(
        "nm_item",
        searchBarcodeData[0].nm_item,
        "noEvent"
      );
      addedNode?.setDataValue(
        "dc_item_spec",
        searchBarcodeData[0].dc_item_spec,
        "noEvent"
      );
      addedNode?.setDataValue(
        "dc_item_unit",
        searchBarcodeData[0].dc_item_unit,
        "noEvent"
      );
      addedNode?.setDataValue(
        "fg_unit",
        searchBarcodeData[0].fg_unit,
        "noEvent"
      );
      addedNode?.setDataValue("qt_good", qtInv, "noEvent");
      addedNode?.setDataValue("qt", qtInv, "noEvent");
      linkWobillLine(addedNode, searchBarcodeData[0].cd_item);

      itemNodeData = addedNode?.data;
    }
    // 있으면 수량 추가
    else {
      itemNode.setDataValue("qt_good", itemNodeData.qt_good + qtInv, "noEvent");
      itemNode.setDataValue("qt", itemNodeData.qt + qtInv, "noEvent");
    }

    if (searchBarcodeData.length > 0) {
      const invItemList: any = [];
      searchBarcodeData.forEach((item: any) => {
        if (item.cd_item === cdItem) {
          switch (item.fg_unit) {
            case "LOT":
              {
                const newItem = {
                  dt_io: item.dt_io,
                  no_barcode: item.no_lot,
                  qt_inv: 0,
                  qt_isu: 0, // 적용량
                  no_ct: item.no_ct,
                  no_plt: item.no_plt,
                  // no_lot_cust: item.no_lot_cust,
                  cd_item: item.cd_item,
                  cd_wh: item.cd_wh,
                  fg_unit: item.fg_unit,
                  no_src: "", // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_line_src: -1, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_origin: "", // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  no_line_origin: -1, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                };
                invItemList.push(newItem);
              }
              break;
            case "SN":
              {
                const newItem = {
                  dt_io: item.dt_io,
                  no_barcode: item.no_sn,
                  qt_inv: 0,
                  qt_isu: 1, // 적용량
                  no_ct: item.no_ct,
                  no_plt: item.no_plt,
                  // no_lot_cust: "",
                  cd_item: item.cd_item,
                  cd_wh: item.cd_wh,
                  fg_unit: item.fg_unit,
                  no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                };
                invItemList.push(newItem);
              }
              break;
          }

          const newRow = {
            cd_firm: itemNodeData.cd_firm,
            cd_biz: itemNodeData.cd_biz,
            no_io: itemNodeData.no_io,
            no_line: itemNodeData.no_line,
            dt_io: itemNodeData.dt_io,
            tp_slip: itemNodeData.tp_slip,
            fg_slip: itemNodeData.fg_slip,
            fg_io: itemNodeData.fg_io,
            fg_money: itemNodeData.fg_money,
            cd_io: itemNodeData.cd_io,
            cd_item: itemNodeData.cd_item,
            cd_wh: itemNodeData.cd_wh,
            no_barcode: item.no_lot !== "" ? item.no_lot : item.no_sn,
            fg_unit: item.fg_unit,
            qt_good: item.qt_inv,
            no_ct: item.no_ct,
            no_plt: item.no_plt,
            no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
            no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
            dt_io_origin: item.dt_io, // 원 LOT 입고일자
            no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            // no_lot_cust: item.no_lot_cust, // 원 고객사 LOT
          };
          getGridCurrent("grid4").addRow(newRow);
        }
      });

      const prevInvData: any = [];
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        prevInvData.push(node.data);
      });
      setLotSnData([...prevInvData, ...invItemList]);
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeH.data.cd_wh,
      ...pjtFilter(focusedRowNodeL.data.cd_pjt),
      qt_inv: "notZero",
    });

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeH.data.cd_wh,
          ...pjtFilter(focusedRowNodeL.data.cd_pjt),
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "Fifo":
        selectInv("F", "");
        break;
      case "btn_POP3":
        // 신규행이 없으면 Dialog로 띄우기
        const qtiohChanges = getGridCurrent("grid1").getChange().insert;
        if (qtiohChanges.length === 0) {
          await showDialog("alter", "신규행을 추가하시기 바랍니다.");
          return;
        }

        // searchPanel에서 선택된 값 전달
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        // 신규행 포커스 잡아주기
        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        const component = (
          <SN_MTL_MOW_REG_005_POP_003 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "작업지시도움",
          children: component,
        });

        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        if (headerResult.length > 0) {
          setWobillLineData(lineResult);
          setWoNoText(headerResult[0].no_wo); // 작지 SnTextBox에 작업지시번호 표시
        } else {
          resetWobillData();
        }
        break;
    }
  };
  //#endregion

  //#region onSnTextBoxEnter
  const onSnTextBoxEnter = async (e: any) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    // searchPanel에서 선택된 값 전달
    const selectedFormData = searchRef.current.getFormdata();
    const selectedData = {
      cd_biz: selectedFormData.cd_biz,
      dt_from: selectedFormData.dt_from,
      dt_to: selectedFormData.dt_to,
      no_wo: e,
    };

    // 신규행 포커스 잡아주기
    let focusedRowHIdx = 0;
    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (node.data.state === "insert") {
        focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
      }
    });

    const component = (
      <SN_MTL_MOW_REG_005_POP_003 selectedData={selectedData} />
    );
    const result = await showResizablePopupModal({
      title: "작업지시도움",
      children: component,
    });

    if (!result || result === "close") return;

    const headerResult = result[0];
    const lineResult = result[1];

    if (headerResult.length > 0) {
      setWobillLineData(lineResult);
      setWoNoText(headerResult[0].no_wo); // 작지 SnTextBox에 작업지시번호 표시
    } else {
      resetWobillData();
    }
  };
  //#endregion

  //#region Method
  const selectInv = (id: string, barcode: string) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        if (numberBoxFifoRef.current.getValue == 0) {
          showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = Number(numberBoxFifoRef.current.getValue);
        break;
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          selectedMap.set(key, Number(row.qt_isu) || 0);
        });
        break;
      case "S": // 스캔
        // 요청량 있으면 요청량이 잔량
        if (focusedRowNodeL.data.qt_req !== 0) {
          qtRemain = focusedRowNodeL.data.qt_req - focusedRowNodeL.data.qt;

          if (qtRemain === 0) {
            showDialog("alter", "요청수량이 다 등록되었습니다.");
            return false;
          }
        }
        // 요청량이 없으면 바코드 전체 선택
        else {
          qtRemain = -1;
        }
        break;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 요청량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_io: focusedRowNodeL.data?.no_io,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeL.data?.dt_io,
        tp_slip: focusedRowNodeL.data?.tp_slip,
        fg_slip: focusedRowNodeL.data?.fg_slip,
        fg_io: focusedRowNodeL.data?.fg_io,
        fg_money: focusedRowNodeL.data?.fg_money,
        cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: focusedRowNodeL.data?.cd_wh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_src: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_src: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
        no_origin: node.data.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: node.data.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        cd_pjt: focusedRowNodeL.data?.cd_pjt ?? "", // 라인 프로젝트(도착 프로젝트 없음)
        no_batch: node.data.no_batch, // 26.11.02 추가
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          sameInvKey(node4.data, focusedRowNodeL.data) &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_good",
      focusedRowNodeL.data?.qt_good + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeH.data.cd_wh,
      ...pjtFilter(focusedRowNodeL.data.cd_pjt),
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          sameInvKey(node4.data, node.data) &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid3").applyFilters({
      cd_item: "",
      cd_wh: "",
      ...pjtFilter(""),
    });
  };

  const resetWobillData = (): any => {
    setWobillLineData([]);
    setWoNoText(""); // 작지 SnTextBox 초기화
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: lotSnData,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
          onModelUpdated: onModelUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onCellValueChanged: onCellValueChangedD,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "불출정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"작업지시적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP2");
                  }}
                />
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="55.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                events={grids.grid2.events}
                filter={{
                  refs: [gridRefs.current["grid4"]],
                  columns: ["no_io", "no_line"],
                }}
                header={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div>
                      <SnTextBox
                        defaultValue={woNoText}
                        onValueChanged={setWoNoText}
                        onEnter={onSnTextBoxEnter}
                      ></SnTextBox>
                    </div>
                    <div>
                      <SnButton
                        text="작지도움"
                        onClick={() => {
                          onSnButtonClick("btn_POP3");
                        }}
                      />
                    </div>
                    <div style={{ width: 40 }}>
                      <SnLabel text="바코드" type="string" />
                    </div>
                    <div>
                      <SnBarcodeBox
                        onEnter={onDirectBarcodeEnter}
                        inputRef={txtDirectBarcodeRef}
                      />
                    </div>
                  </div>
                }
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={lotSnData} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "재고리스트",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid3.events}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <SnSwitch
                        ref={switchRef}
                        labelCheck="검색"
                        labelUncheck="스캔"
                        defaultChecked={false}
                      />
                      <div>
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter}
                          inputRef={txtBarcodeRef}
                        />
                      </div>
                      <div className="w-[80px]">
                        <SnNumberBox
                          inputRef={numberBoxFifoRef}
                          customFormat="QT"
                        />
                      </div>
                      <div>
                        <SnButton
                          text="선입선출"
                          onClick={() => {
                            onSnButtonClick("Fifo");
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single", title: "출고정보" }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MOW_REG_005;
